import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Row, Col, Space, Breadcrumb, Typography, Spin } from 'antd';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { apiRequest } from '../../../../service/apiRequest';

const { Link } = Typography;

const PaymentSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [upiVerifying, setUpiVerifying] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await apiRequest({ method: 'get', url: '/payment-setting' });
        console.log('Fetched payment settings:', response);
        if (response) {
          form.setFieldsValue(response);  // <-- This is correct
        }
      } catch (error) {
        showError('Failed to load payment settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [form]);

  const handleSave = async (values: any) => {
    try {
      await apiRequest({
        method: 'put',
        url: '/save-payment-settings',
        data: values,
      });
      showSuccess('Payment settings saved successfully');
    } catch (error) {
      showError('Failed to save payment settings');
    }
  };

  const handleVerifyUPI = async () => {
    try {
      const ps_upi_id = form.getFieldValue('ps_upi_id');
      if (!ps_upi_id) {
        showError('Please enter UPI ID first');
        return;
      }

      setUpiVerifying(true);
      const response = await apiRequest({
        method: 'post',
        url: '/verify-upi',
        data: { vpa: ps_upi_id },
      });

      if (response?.data?.success) {
        showSuccess('UPI ID is valid');
      } else {
        showError('UPI ID is invalid');
      }
    } catch (error) {
      showError('UPI validation failed');
    } finally {
      setUpiVerifying(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Breadcrumb items={[{ title: 'Dashboard' }, { title: 'Payment Settings' }]} />
      <Row gutter={16} justify="center">
        <Col span={12}>
          <Card title="Payment Settings">
            <Form layout="vertical" form={form} onFinish={handleSave}>
              <Form.Item label="Payment Gateway" name="ps_gateway" rules={[{ required: true }]}>
                <Input placeholder="Enter payment gateway" />
              </Form.Item>

              <Form.Item label="Prefix" name="ps_prefix">
                <Input placeholder="Enter prefix (optional)" />
              </Form.Item>

              <Form.Item label="Bank Account Number" name="ps_account_no" rules={[{ required: true }]}>
                <Input placeholder="Enter bank account number" />
              </Form.Item>

              <Form.Item label="Bank Name" name="ps_bank_name">
                <Input placeholder="Enter bank name (optional)" />
              </Form.Item>

              <Form.Item label="IFSC Code" name="ps_ifsc_code" rules={[{ required: true }]}>
                <Input placeholder="Enter IFSC code" />
              </Form.Item>

              <Form.Item label="MICR Code" name="ps_micr_code">
                <Input placeholder="Enter MICR code (optional)" />
              </Form.Item>

              <Form.Item
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>UPI ID</span>
                    <Link onClick={handleVerifyUPI} disabled={upiVerifying}>
                      {upiVerifying ? 'Verifying...' : 'Verify'}
                    </Link>
                  </div>
                }
                name="ps_upi_id"
                colon={false}
              >
                <Input placeholder="Enter UPI ID" />
              </Form.Item>

              <Form.Item label="Razorpay Key" name="ps_api_key" rules={[{ required: true }]}>
                <Input placeholder="Enter Razorpay key" />
              </Form.Item>

              <Form.Item label="Razorpay Secret" name="ps_secret_key" rules={[{ required: true }]}>
                <Input.Password placeholder="Enter Razorpay secret" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block>
                  Save Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default PaymentSettings;
